import React, { createContext, useContext, useState, ReactNode } from 'react';
import { en } from '../locales/en';
import { fr } from '../locales/fr';
import { nl } from '../locales/nl';
import { de } from '../locales/de';
import { pl } from '../locales/pl';

export type SupportedLanguage = 'en' | 'fr' | 'nl' | 'de' | 'pl';

export interface LocalizationContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: typeof en;
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined);

const translations = {
  en,
  fr,
  nl,
  de,
  pl
};

interface LocalizationProviderProps {
  children: ReactNode;
}

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    // Try to get language from localStorage, fallback to browser language, then default to 'en'
    const savedLanguage = localStorage.getItem('peppol-vat-verifier-language') as SupportedLanguage;
    if (savedLanguage && translations[savedLanguage]) {
      return savedLanguage;
    }
    
    // Try to detect browser language
    const browserLang = navigator.language.split('-')[0];
    if (translations[browserLang as SupportedLanguage]) {
      return browserLang as SupportedLanguage;
    }
    
    return 'en';
  });

  const setLanguage = (lang: SupportedLanguage) => {
    setLanguageState(lang);
    localStorage.setItem('peppol-vat-verifier-language', lang);
  };

  const t = translations[language];

  return (
    <LocalizationContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = (): LocalizationContextType => {
  const context = useContext(LocalizationContext);
  if (context === undefined) {
    throw new Error('useLocalization must be used within a LocalizationProvider');
  }
  return context;
};
